import { useCallback } from 'react';
import { useSettings, useSettingsDispatch } from '../context/SettingsProvider';
import { useUser } from '../context/UserProvider';
import { fetchData } from '../inc/functions';
import { ADD_COMMENT, GET_COMMENT } from '../inc/endpoints';

const isCookieCheckFailed = (response) => {
	return (
		response?.code === 'rest_cookie_invalid_nonce' ||
		(typeof response?.message === 'string' &&
			response.message.toLowerCase().includes('cookie check failed'))
	);
};

const postComment = async (payload, nonce) => {
	const options = {
		method: 'POST',
		credentials: 'include',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			...(nonce ? { 'X-WP-Nonce': nonce } : {}),
		},
		body: JSON.stringify(payload),
	};

	return fetchData(ADD_COMMENT, options);
};

export default function useAsyncDispatchComment(
	commentsContext,
	commentsDispatch
) {
	const POST_ID = commentsContext.postId;
	const userContext = useUser();

	const settings = useSettings();
	const settingsDispatch = useSettingsDispatch();

	const refreshNonce = useCallback(async () => {
		const formData = new URLSearchParams();
		formData.append('action', 'refresh_api_nonce');

		const response = await fetch(settings.ajaxUrl, {
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type':
					'application/x-www-form-urlencoded; charset=UTF-8',
			},
			body: formData,
		});

		const data = await response.json();
		const nonce = data?.success ? data?.data?.nonce : null;

		if (response.ok && nonce) {
			settingsDispatch({
				type: 'update',
				payload: {
					nonce,
				},
			});
			return nonce;
		}

		return null;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const createComment = useCallback(
		async (payload) => {
			if (userContext.type === 'commenter') {
				// If the user is a commenter, we don't need to include the nonce in the request.
				return await postComment(payload, null);
			}

			let nonce = settings.nonce;
			if (!nonce) {
				nonce = await refreshNonce();
			}

			if (!nonce) {
				return {
					message:
						'Unable to create a valid nonce for comment submission.',
				};
			}

			let response = await postComment(payload, nonce);

			if (isCookieCheckFailed(response)) {
				const freshNonce = await refreshNonce();
				if (!freshNonce) {
					return response;
				}

				response = await postComment(payload, freshNonce);
			}

			return response;
		},
		[refreshNonce, settings, userContext]
	);

	return (action) => {
		switch (action.type) {
			case 'init': {
				settingsDispatch({
					type: 'isLoading',
					isLoading: true,
				});

				const params = {
					postId: POST_ID,
					offset: 0,
					order: commentsContext.order,
					email:
						!settings.commentRegistration ||
						userContext.type === 'logged'
							? userContext.email
							: '',
				};

				const urlParams = new URLSearchParams(params).toString();
				fetchData(`${GET_COMMENT}?${urlParams}`).then((data) => {
					data.offset = data.comments[0].children.length;
					commentsDispatch({
						type: 'update',
						payload: data,
					});

					settingsDispatch({
						type: 'isLoading',
						isLoading: false,
					});
				});
				break;
			}
			case 'order': {
				settingsDispatch({
					type: 'isLoading',
					isLoading: true,
				});

				const params = {
					postId: POST_ID,
					offset: 0,
					order: action.order,
					email:
						!settings.commentRegistration ||
						userContext.type === 'logged'
							? userContext.email
							: '',
				};

				const urlParams = new URLSearchParams(params).toString();
				fetchData(`${GET_COMMENT}?${urlParams}`).then((data) => {
					data.order = action.order;
					data.offset = data.comments[0].children.length;

					commentsDispatch({
						type: 'update',
						payload: data,
					});

					settingsDispatch({
						type: 'isLoading',
						isLoading: false,
					});
				});
				break;
			}
			case 'load': {
				settingsDispatch({
					type: 'isLoading',
					isLoading: true,
				});

				const params = {
					postId: POST_ID,
					offset: commentsContext.offset,
					order: commentsContext.order,
					email:
						!settings.commentRegistration ||
						userContext.type === 'logged'
							? userContext.email
							: '',
				};

				const urlParams = new URLSearchParams(params).toString();
				fetchData(`${GET_COMMENT}?${urlParams}`).then((data) => {
					data.offset =
						commentsContext.offset +
						data.comments[0].children.length;

					const rootComments = {
						id: 0,
						children: [
							...commentsContext.comments[0].children,
							...data.comments[0].children,
						],
					};
					data.comments = {
						...commentsContext.comments,
						...data.comments,
						0: { ...rootComments },
					};

					commentsDispatch({
						type: 'update',
						payload: data,
						reset: false,
					});

					settingsDispatch({
						type: 'isLoading',
						isLoading: false,
					});
				});
				break;
			}
			case 'add': {
				settingsDispatch({
					type: 'isLoading',
					isLoading: true,
				});

				const data = {
					post: POST_ID,
					author_name: userContext.name,
					author_email: userContext.email,
					content: action.content,
					parent: action.parentId,
				};

				createComment(data).then((data) => {
					if (typeof data.message !== 'undefined') {
						action.errorHandler(data.message);
					} else if (
						typeof data.status !== 'undefined' &&
						data.status !== 'spam'
					) {
						// Form comment object with necessary fields
						const comment = {
							id: data.id,
							content: data.content.rendered,
							author: userContext.name,
							author_email: userContext.email,
							date: data.date,
							approved: data.status === 'hold' ? false : true,
							children: [],
						};

						// Form parent children array
						const parentChildren =
							data.parent === 0
								? [comment.id].concat(
										commentsContext.comments[data.parent]
											.children
									)
								: [
										...commentsContext.comments[data.parent]
											.children,
									].concat([comment.id]);
						// Form parent object
						const parentComment = {
							...commentsContext.comments[data.parent],
							children: parentChildren,
						};

						// Form comments object
						const comments = {
							...commentsContext.comments,
							[comment.id]: comment,
							[data.parent]: parentComment,
						};

						const payload = {
							comments: comments,
							offset:
								data.parent === 0
									? commentsContext.offset + 1
									: commentsContext.offset,
							count: commentsContext.count + 1,
						};

						action.setComment('');
						settingsDispatch({
							type: 'expandReply',
							expandedReply: null,
						});

						commentsDispatch({
							type: 'update',
							payload: payload,
						});
					} else {
						console.log('Something went wrong: ', data);
					}

					settingsDispatch({
						type: 'isLoading',
						isLoading: false,
					});
				});
				break;
			}
			case 'addNoConsent': {
				settingsDispatch({
					type: 'update',
					payload: {
						isLoading: true,
						isFormExpanded: false,
					},
				});

				const data = {
					post: POST_ID,
					author_name: action.user.name,
					author_email: action.user.email,
					content: settings.deferedComment.content,
					parent: settings.deferedComment.parentId,
				};

				createComment(data).then((data) => {
					if (typeof data.message !== 'undefined') {
						settings.deferedComment.errorHandler(data.message);
					} else if (
						typeof data.status !== 'undefined' &&
						data.status !== 'spam'
					) {
						// Form comment object with necessary fields
						const comment = {
							id: data.id,
							content: data.content.rendered,
							author: action.user.name,
							author_email: action.user.email,
							date: data.date,
							approved: data.status === 'hold' ? false : true,
							children: [],
						};

						// Form parent children array
						const parentChildren =
							data.parent === 0
								? [comment.id].concat(
										commentsContext.comments[data.parent]
											.children
									)
								: [
										...commentsContext.comments[data.parent]
											.children,
									].concat([comment.id]);
						// Form parent object
						const parentComment = {
							...commentsContext.comments[data.parent],
							children: parentChildren,
						};

						// Form comments object
						const comments = {
							...commentsContext.comments,
							[comment.id]: comment,
							[data.parent]: parentComment,
						};

						const payload = {
							comments: comments,
							offset:
								data.parent === 0
									? commentsContext.offset + 1
									: commentsContext.offset,
							count: commentsContext.count + 1,
						};

						settings.deferedComment.setComment('');

						settingsDispatch({
							type: 'update',
							payload: {
								expandedReply: null,
							},
						});

						commentsDispatch({
							type: 'update',
							payload: payload,
						});
					} else {
						console.log('Something went wrong: ', data);
					}

					settingsDispatch({
						type: 'update',
						payload: {
							isLoading: false,
							deferedComment: null,
						},
					});
				});
				break;
			}
			default: {
				throw Error('Unknown async comment action: ' + action.type);
			}
		}
	};
}
